import { useCallback } from 'react';
import useCompany from 'hooks/data/company/useCompany';
import useUser from 'hooks/data/users/useUser';
import { useUserInfo } from 'pages/UserProvider';
import { isEntitlementApp, isOptInEntitlementApp } from 'utils/schemas/companySchema';
import { DIQAdminRoleName, DIQSupportRoleName } from 'utils/userRoles';

/** Internal staff hold no seats in a customer's company, so they are never app-gated. */
const INTERNAL_ROLES: readonly string[] = [DIQAdminRoleName, DIQSupportRoleName];

/** The synthetic app every user carries. Never a product grant. */
const CORE_CLIENT_ID = 'core';

export type AppAccess = Readonly<{
  /**
   * The company owns the app AND the signed-in user may use it.
   * This is the field for nav visibility and route guards.
   */
  hasApp: boolean;
  /**
   * The company owns the app, ignoring the per-user seat grant. Use for capability/config UI
   * ("does this company run canvassing?"), never for authorization -- an admin without a
   * personal seat still configures the product on the company's behalf.
   */
  companyOwnsApp: boolean;
  /** True until every query this answer depends on has resolved. Never reject while true. */
  isPending: boolean;
}>;

/**
 * Single source of truth for app entitlement.
 *
 * Access = the company owns the app AND the user holds it. Entitlement apps (stored with
 * `seats = NULL`, e.g. presentations) are granted company-wide, so ownership alone is access.
 *
 * `useUserInfo().company` is not trusted to carry `apps` (see UserProvider, MDB-2100), so the
 * company is re-fetched via `useCompany`. Per-user grants live on `UserV2Response.apps`, which
 * `CurrentUserResponse` does not carry, so the user is re-fetched via `useUser`.
 *
 * The hook returns a factory so a single call site can ask about several apps while hoisting
 * both queries once -- AppSidebar asks about three.
 */
export default function useHasApp(): (clientId: string) => AppAccess {
  const { user, company: userCompany } = useUserInfo();

  const companyId = userCompany?.id;
  const { data: company, isPending: companyPending, isError: companyError } = useCompany(companyId);

  // Two independent bypasses for the per-user half of the check:
  //  - internal roles (DIQAdmin/DIQSupport) are never gated -- useSeats deliberately excludes
  //    them from seat counting, so they hold no seats by design;
  //  - anyone whose selected company differs from their own is spoofing, so their own `apps`
  //    list describes a different tenant and says nothing about this one.
  // `roleName` is safe to read during a spoof: it comes from /admin/user/me, which is in
  // useAuthenticatedUserClient's routesRequireUserToken and so always uses the real token. Do
  // not derive this from the decoded JWT -- spoof tokens hardcode the role to "Admin".
  const isInternal = INTERNAL_ROLES.includes(user?.roleName ?? '');
  const isSpoofing = Boolean(user && userCompany && userCompany.id !== user.company?.id);
  const skipSeatCheck = isInternal || isSpoofing;

  // Passing undefined skips the request entirely. That matters during a spoof: GET
  // /api/v2.0/admin/user/{id} is NOT in routesRequireUserToken, so it would run on the spoof
  // token and ask the customer's tenant for a record that lives in the internal company.
  const userId = skipSeatCheck ? undefined : user?.id;
  const { data: userRecord, isPending: userPending, isError: userError } = useUser(userId);

  // Neither query has an `enabled` guard: with an undefined id the query still runs and
  // resolves to null, so `isPending` alone cannot tell "no id yet" from "loaded, empty".
  // Readiness therefore requires an id. An errored query counts as resolved and falls open.
  const companyReady = Boolean(companyId) && (!companyPending || companyError);
  const userReady = skipSeatCheck || (Boolean(userId) && (!userPending || userError));

  return useCallback(
    (clientId: string): AppAccess => {
      const companyApps = company?.apps ?? [];
      const ownsNamedApp = companyApps.some((app) => app.clientId === clientId);
      // An empty list means "unknown", not "unsold": the backend treats `apps` as a full
      // replacement and company edit forms have historically shipped it empty, so treating []
      // as a denial would black out an entire company on a bad save. The entitlements that existed
      // before opt-in shipped were granted to every company on create and backfilled, so absence
      // there means missing data, not a withheld grant -- and the API still refuses anyone who
      // genuinely lacks it.
      //
      // Opt-in entitlements are the exception and must fail closed. Nothing auto-grants them
      // (CreateCompanyDialog ships `entitlements: ['presentations']`), so [] is authoritative. Under
      // the fail-open a company with no grant cleared both `RequireApp` and the sidebar check, so its
      // Admin saw AI Visibility and the setup form and only hit "You do not have permission" once the
      // API refused. Hiding a genuinely-granted app after a bad save is the safer direction here.
      const companyOwnsApp = isOptInEntitlementApp(clientId) ? ownsNamedApp : companyApps.length === 0 || ownsNamedApp;

      // Company-wide grants have no seat, so there is no user query to wait on.
      if (skipSeatCheck || isEntitlementApp(clientId)) {
        return { hasApp: companyOwnsApp, companyOwnsApp, isPending: !companyReady };
      }

      const heldApps = (userRecord?.apps ?? []).filter((app) => app !== CORE_CLIENT_ID);

      // Fail open on absence of signal, closed only on a positive contradicting signal:
      //  - `apps` undefined (field absent, or the lookup errored) -> no signal -> allow;
      //  - `apps` [] or ['core'] -> holds no product app at all, indistinguishable from a
      //    legacy account that was never assigned any -> allow;
      //  - company owner -> always holds everything the company owns;
      //  - `apps` naming >= 1 product app -> authoritative -> deny anything absent from it.
      // Self-healing: once a user is explicitly assigned any app, gating becomes exact.
      const userHoldsApp = userRecord?.owner === true || heldApps.length === 0 || heldApps.includes(clientId);

      return {
        hasApp: companyOwnsApp && userHoldsApp,
        companyOwnsApp,
        isPending: !companyReady || !userReady,
      };
    },
    [company?.apps, userRecord?.apps, userRecord?.owner, skipSeatCheck, companyReady, userReady]
  );
}
